<template>
  <div v-bind="rootAttrs">
    <div class="flex flex-row md:flex-col md:gap-1 gap-10">

        <span v-if="description" class="text-sm text-gray-500 !font-just">
          {{ description }}
        </span>

      <ToggleSwitch
        v-bind="controlAttrs"
        :modelValue="internalValue"
        :disabled="disabled"
        :id="id"
        :name="name"
        :aria-label="label || 'Toggle Switch'"
        :data-test-id="`${id}-switch`"
        @update:modelValue="handleChange"
        :pt="{
          root: { class: 'relative inline-flex items-center justify-center' },
          slider: {
            class: 'w-11 h-6 transition-colors duration-200',
            style: sliderStyle(),
          },
          thumb: {
            class:
              'w-5 h-5 rounded-full transition-transform duration-200 bg-white',
          },
        }"
      />
    </div>

    <Message
      v-if="invalid && errorMessage"
      severity="error"
      size="small"
      variant="simple"
      :class="{ '!text-error !px-[10px] !mt-[3.84px]': invalid }"
      :data-test-id="`${id}-errorMessage`"
    >
      {{ errorMessage }}
    </Message>
  </div>
</template>

<script setup>
// Forward fallthrough attrs (tabindex, @focus/@click) onto the inner control via
// v-bind="$attrs", not the wrapper <div>, so a parent-supplied tabindex (e.g.
// from CustomField) doesn't create a second tab stop. See BaseInput.vue.
defineOptions({ inheritAttrs: false });

import { onMounted, ref, watch } from "vue";
import { useControlAttrs } from "@/composables/useControlAttrs";

// class/style -> root (layout); the rest -> inner control (tabindex/listeners).
const { rootAttrs, controlAttrs } = useControlAttrs();
import BaseTooltip from "./BaseTooltip.vue";
const props = defineProps({
  modelValue: { type: Boolean, default: false },
  label: { type: String, default: "" },
  description: { type: String, default: "" },
  id: { type: String, default: "" },
  name: { type: String, default: "switch" },
  required: { type: Boolean, default: false },
  errorMessage: { type: String, default: "" },
  invalid: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  showInfoSvg: { type: Boolean, default: false },
  tooltip: { type: String, default: "" },
});

const emit = defineEmits(["update:modelValue", "change", "focus", "blur"]);
const internalValue = ref(false);

watch(
  () => props.modelValue,
  (val) => {
    if (val !== internalValue.value) internalValue.value = val;
  }
);

const handleChange = (value) => {
  internalValue.value = value;
  emit("update:modelValue", value);
  emit("change", value);
};

const sliderStyle = () =>
  internalValue.value
    ? {
        background: "linear-gradient(90deg, #08F480 0%, #05D4F4 100%)",
        borderRadius: "15.358px",
      }
    : {
        background: "linear-gradient(270deg, #494949 -2.02%, #6C6C6C 100%)",
        borderRadius: "15.358px",
      };

onMounted(() => {
  internalValue.value = props.modelValue;
});
</script>
